import type { TtqibViewport } from './types';
import {
  TTQIB_BIRDS_EYE_EXIT_ZOOM_THRESHOLD,
  TTQIB_ZOOM_MAX,
  TTQIB_ZOOM_MIN,
} from './ttqibViewport';

/**
 * 将单张卡片放大并居中到视口，尽量铺满可用区域以便阅读正文。
 */
export function computeFocusCardViewport(
  card: { x: number; y: number; width: number; height: number },
  viewW: number,
  viewH: number,
  options?: {
    padding?: number;
    topChrome?: number;
    maxZoom?: number;
    minZoom?: number;
  },
): TtqibViewport {
  const padding = options?.padding ?? 24;
  const topChrome = options?.topChrome ?? 72;
  const maxZoom = options?.maxZoom ?? 2.5;
  const minZoom = options?.minZoom ?? 0.5;
  const cardW = Math.max(Number(card.width) || 0, 160);
  const cardH = Math.max(Number(card.height) || 0, 160);
  const usableW = Math.max(viewW - padding * 2, 160);
  const usableH = Math.max(viewH - padding * 2 - topChrome, 160);
  const zoom = Math.min(maxZoom, Math.max(minZoom, Math.min(usableW / cardW, usableH / cardH)));
  const contentMidY = topChrome + (viewH - topChrome) / 2;
  return {
    x: viewW / 2 - (card.x + cardW / 2) * zoom,
    y: contentMidY - (card.y + cardH / 2) * zoom,
    zoom,
  };
}

/**
 * Scan / 聚焦：在当前 LOD 内居中，禁止一对多 ↔ 多对一跳变。
 * - 多对一：只平移、保持 zoom（避免 screenBoost 牵动收敛/一解多题落位）
 * - 一对多：可放大阅读，但 zoom 不得低于退出多对一的迟滞上沿
 */
export function computeFocusCardViewportInLod(
  card: { x: number; y: number; width: number; height: number },
  viewW: number,
  viewH: number,
  options: {
    birdsEyeMode: boolean;
    currentZoom: number;
    padding?: number;
    topChrome?: number;
  },
): TtqibViewport {
  if (options.birdsEyeMode) {
    const zoom = Math.min(
      TTQIB_BIRDS_EYE_EXIT_ZOOM_THRESHOLD - 0.01,
      Math.max(TTQIB_ZOOM_MIN, options.currentZoom),
    );
    return computeFocusCardViewport(card, viewW, viewH, {
      padding: options.padding,
      topChrome: options.topChrome,
      minZoom: zoom,
      maxZoom: zoom,
    });
  }
  return computeFocusCardViewport(card, viewW, viewH, {
    padding: options.padding,
    topChrome: options.topChrome,
    minZoom: Math.max(TTQIB_BIRDS_EYE_EXIT_ZOOM_THRESHOLD, TTQIB_ZOOM_MIN),
    maxZoom: TTQIB_ZOOM_MAX,
  });
}

export function touchListMidpoint(touches: TouchList): { x: number; y: number } {
  let x = 0;
  let y = 0;
  for (let i = 0; i < touches.length; i += 1) {
    x += touches[i].clientX;
    y += touches[i].clientY;
  }
  const n = Math.max(touches.length, 1);
  return { x: x / n, y: y / n };
}

export function pointerMapMidpoint(
  pointers: Map<number, { x: number; y: number }>,
): { x: number; y: number } {
  let x = 0;
  let y = 0;
  for (const point of pointers.values()) {
    x += point.x;
    y += point.y;
  }
  const n = Math.max(pointers.size, 1);
  return { x: x / n, y: y / n };
}
